import Button from "../../../components/Button";
import ProgressBar from "../../../components/ProgressBar";
import { useNavigate } from "react-router-dom";
import { BlobServiceClient } from "@azure/storage-blob";
import TextArea from "../../../components/TextArea";
import AddIcon from "/public/images/icons/add.svg?react";
import VoiceNote from "../../../components/VoiceNote";
import PropTypes from "prop-types";
import { useState, useEffect } from "react";
import AzureProfilePictureUpload from "../../../components/AzureProfilePictureUpload";
import TextInput from "../../../components/TextInput";
import { useDrawerContext } from "../../../context/drawerContext";
import CloseIcon from "/public/images/icons/close.svg?react";
import { useUserContext } from "../../../context/userContext";
import BinIcon from "/public/images/icons/trashRed.svg?react";
import SelectInput from "../../../components/SelectInput";
import { days, months, years } from "../../../utils/calendar"
import { PhoneNumberUtil } from 'google-libphonenumber';
import AdvancedPhoneInput from "../../../components/AdvancedPhoneInput";
import Toggle from "../../../components/Toggle";
import fetchWithRefresh from "../../../utils/fetchWithRefresh";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";
import { LogErrors } from "../../../utils/LogErrors";

const phoneUtil = PhoneNumberUtil.getInstance();

export default function Step2({ band, profile, emergencyContacts, medicalHistory, allergies, medications }) {
  const navigate = useNavigate();
  const { setDrawerContent, setOpen } = useDrawerContext();
  const { user, logout, updateTokens } = useUserContext();
  const [phoneNumberErrors, setPhoneNumberErrors] = useState([]);
  const [participantNameWarning, setParticipantNameWarning] = useState(false);
  const [primaryICEWarning, setPrimaryICEWarning] = useState(false);
  const [participantPrimaryWarning, setParticipantPrimaryWarning] = useState(false);
  const [uploadingCDN, setUploadingCDN] = useState(false);
  const [bandData, setBandData] = useState(null);
  const [localStepAnswers, setLocalStepAnswers] = useState(localStorage.getItem("activeBandStep2") ? JSON.parse(localStorage.getItem("activeBandStep2")) : null);
  const [updatedEmergencyContacts, setUpdatedEmergencyContacts] = useState(localStepAnswers?.additionalICEContacts || emergencyContacts || []);
  const [deletedEmergencyContacts, setDeletedEmergencyContacts] = useState(localStorage.getItem("activeBandStep2Delete") ? JSON.parse(localStorage.getItem("activeBandStep2Delete")) : []);
  const [selectedDay, setSelectedDay] = useState(
    localStepAnswers?.participantDOB.split("-")[2] || profile?.DateOfBirth.split("-")[2] || ''
  );

  const [selectedMonth, setSelectedMonth] = useState(
    localStepAnswers?.participantDOB.split("-")[1] || profile?.DateOfBirth.split("-")[1] || ''
  );

  const [selectedYear, setSelectedYear] = useState(
    localStepAnswers?.participantDOB.split("-")[0] || profile?.DateOfBirth.split("-")[0] || ''
  );

  const [participantDOB, setParticipantDOB] = useState(
    localStepAnswers?.participantDOB || profile?.DateOfBirth || ''
  );

  useEffect(() => {
    if (selectedDay && selectedMonth && selectedYear) {
      setParticipantDOB(`${selectedYear}-${selectedMonth}-${selectedDay}`);
    }
  }, [selectedDay, selectedMonth, selectedYear]);
  
  useEffect(() => {
    fetchBand();
  }, [])

  function base64ToFile(base64Data, fileName, fileType) {
    // Decode the base64 string
    const byteCharacters = atob(base64Data.split(',')[1]); // Split to remove the data URL part (data:image/png;base64,)
    
    // Create a byte array from the decoded base64 string
    const byteArray = new Uint8Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
        byteArray[i] = byteCharacters.charCodeAt(i);
    }

    // Create a Blob from the byte array
    const blob = new Blob([byteArray], { type: fileType });
    
    // Create a File object from the Blob, using the specified fileName
    const file = new File([blob], fileName, { type: fileType });

    return file;
  }

  const validateForm = async (event) => {
    event.preventDefault();
    const formDetails = new FormData(event.currentTarget);
    
    if(!formDetails.get('participantDOB')){
      dobNotSet();
      return;
    }
  
    if(!formDetails.get('uploadedProfilePicture')){
      profilePictureNotSet();
      return;
    } else {
      const uploadedProfilePicture = formDetails.get('uploadedProfilePicture');
      const uploadedProfilePictureName = formDetails.get('uploadedProfilePictureName');

      if (!uploadedProfilePicture.includes('blob.core.windows.net')) {
        setUploadingCDN(true);

        const fileName = uploadedProfilePictureName || `profile-picture.png`; // fallback if needed
        const file = base64ToFile(uploadedProfilePicture, fileName, "image/png");

        const account = import.meta.env.VITE_AZURE_STORAGE_ACCOUNT;
        const sasKey = import.meta.env.VITE_AZURE_PROFILE_PICTURE_STORAGE_SAS_TOKEN;
        const container = import.meta.env.VITE_AZURE_PROFILE_PICTURE_STORAGE;

        try {
            const blobServiceClient = new BlobServiceClient(`https://${account}.blob.core.windows.net?sv=${sasKey}`);
            const containerClient = blobServiceClient.getContainerClient(container);

            const lastDotIndex = fileName.lastIndexOf('.');
            const fileExtension = lastDotIndex > 0 ? fileName.substring(lastDotIndex + 1) : 'png';

            const blobName = `${band?.ParticipantId}-profile-picture.${fileExtension}`;
            const blockBlobClient = containerClient.getBlockBlobClient(blobName);
            const arrayBuffer = await file.arrayBuffer();
            const uploadBlobResponse = await blockBlobClient.upload(arrayBuffer, arrayBuffer.byteLength);

            if (uploadBlobResponse._response.status === 201) {
                const uploadedBlobUrl = `https://${account}.blob.core.windows.net/${container}/${blobName}`;
                formDetails.set('uploadedProfilePicture', uploadedBlobUrl);
            } else {
              setOpen(true);
              setUploadingCDN(false);
              setDrawerContent(
                <div className="w-full">
                  <div className="flex items-center justify-between w-full">
                    <br />
                    <h2 className="leading-5 tracking-widest uppercase text-[12px]">PROFILE PICTURE</h2>
                    <CloseIcon className='cursor-pointer' onClick={() => {setOpen(false);}} />
                  </div>
                  <div className="mt-6 flex flex-col gap-2 justify-stretch">
                    <h3 className="text-[24px]">We couldn&apos;t upload your profile picture</h3>
                    <p className="text-sportsDarkGrey mb-[26px]">There was an issue uploading your profile picture, please try selecting a new profile picture.</p>
                    <Button variant='primary' onClick={() => setOpen(false)}>Close</Button>
                  </div>
                </div>
              ); return;
            }
        } catch(error) {
          setOpen(true);
          setUploadingCDN(false);
          setDrawerContent(
            <div className="w-full">
              <div className="flex items-center justify-between w-full">
                <br />
                <h2 className="leading-5 tracking-widest uppercase text-[12px]">PROFILE PICTURE</h2>
                <CloseIcon className='cursor-pointer' onClick={() => {setOpen(false);}} />
              </div>
              <div className="mt-6 flex flex-col gap-2 justify-stretch">
                <h3 className="text-[24px]">We couldn&apos;t upload your profile picture</h3>
                <p className="text-sportsDarkGrey mb-[26px]">There was an issue uploading your profile picture, please try selecting a new profile picture.</p>
                <Button variant='primary' onClick={() => setOpen(false)}>Close</Button>
              </div>
            </div>
          ); return;
        }
      }
    }

    if(formDetails.get('uploadedVoiceNote')){
      if (!formDetails.get('uploadedVoiceNote').includes('blob.core.windows.net')) {
        setUploadingCDN(true);
        const res = await fetch(formDetails.get('uploadedVoiceNote'));
        const blob = await res.blob();

        const account = import.meta.env.VITE_AZURE_STORAGE_ACCOUNT;
        const sasKey = import.meta.env.VITE_AZURE_VOICE_NOTE_STORAGE_SAS_TOKEN;
        const container = import.meta.env.VITE_AZURE_VOICE_NOTE_STORAGE;
  
        try {
            const blobServiceClient = new BlobServiceClient(`https://${account}.blob.core.windows.net?sv=${sasKey}`);
            const containerClient = blobServiceClient.getContainerClient(container);
            const blobName =  band?.ParticipantId + '-voice-note.mp3';
            const blockBlobClient = containerClient.getBlockBlobClient(blobName);
            const arrayBuffer = await blob.arrayBuffer();
            const uploadBlobResponse = await blockBlobClient.upload(arrayBuffer, arrayBuffer.byteLength);
  
            if (uploadBlobResponse._response.status === 201) {
                const uploadedBlobUrl = `https://${account}.blob.core.windows.net/${container}/${blobName}`;
                formDetails.set('uploadedVoiceNote', uploadedBlobUrl);
            } else {
                setOpen(true);
                setUploadingCDN(false);
                setDrawerContent(
                  <div className="w-full">
                    <div className="flex items-center justify-between w-full">
                      <br />
                      <h2 className="leading-5 tracking-widest uppercase text-[12px]">VOICE NOTE</h2>
                      <CloseIcon className='cursor-pointer' onClick={() => {setOpen(false);}} />
                    </div>
                    <div className="mt-6 flex flex-col gap-2 justify-stretch">
                      <h3 className="text-[24px]">We couldn&apos;t upload your voice note</h3>
                      <p className="text-sportsDarkGrey mb-[26px]">There was an issue uploading your audio file, please try recording a new voice note.</p>
                      <Button variant='primary' onClick={() => setOpen(false)}>Close</Button>
                    </div>
                  </div>
                ); return;
            }
        } catch(error) {
          setOpen(true);
          setUploadingCDN(false);
          setDrawerContent(
            <div className="w-full">
              <div className="flex items-center justify-between w-full">
                <br />
                <h2 className="leading-5 tracking-widest uppercase text-[12px]">VOICE NOTE</h2>
                <CloseIcon className='cursor-pointer' onClick={() => {setOpen(false);}} />
              </div>
              <div className="mt-6 flex flex-col gap-2 justify-stretch">
                <h3 className="text-[24px]">We couldn&apos;t upload your voice note</h3>
                <p className="text-sportsDarkGrey mb-[26px]">There was an issue uploading your audio file, please try recording a new voice note.</p>
                <Button variant='primary' onClick={() => setOpen(false)}>Close</Button>
              </div>
            </div>
          ); return;
        }
      }
    }

    const phoneErrors = [];

    try {
      const validatePhoneNumber = phoneUtil.parseAndKeepRawInput(formDetails.get('bandICEPhoneNumber'));
      const validPhoneNumber = phoneUtil.isValidNumber(validatePhoneNumber);
  
      if (!validPhoneNumber) {
        phoneErrors.push({ name: 'bandICEPhoneNumber', error: 'This is not a valid phone number.' });
      }
    } catch {
      phoneErrors.push({ name: 'bandICEPhoneNumber', error: 'This is not a valid phone number.' });
    }

    if(formDetails.get('voiceNoteRemoved') === "true"){
      formDetails.set('uploadedVoiceNote', "");
    }

    setUploadingCDN(false);

    const structureFormDetails = {
      "profilePicture": formDetails.get('uploadedProfilePicture'),
      "participantName": formDetails.get('participantName'),
      "participantDOB": formDetails.get('participantDOB'),
      "bandNickname": formDetails.get('bandNickname'),
      "bandICEName": formDetails.get('bandICEName'),
      "bandICEPhoneNumber": formDetails.get('bandICEPhoneNumber'),
      "EmergencySMSPrimaryContact": formDetails.get('EmergencySMSPrimaryContactValue'),
      "bandICERelation": formDetails.get('bandICERelation'),
      "participantNotes": formDetails.get('participantNotes'),
      "PrimaryMedicalCondition": formDetails.get('bandPrimaryCondition'),
      "uploadedVoiceNote":  formDetails.get('uploadedVoiceNote'),
    };
    
    const additionalICEContacts = updatedEmergencyContacts?.map((_, index) => {
      const iceId = formDetails.get(`bandAdditionalICEID${index + 1}`);

      return {
        additionalICEContactId: iceId ? iceId : "", // Ensure it defaults to an empty string if missing
        name: formDetails.get(`bandAdditionalICEName${index + 1}`),
        phoneNumber: formDetails.get(`bandAdditionalICEPhoneNumber${index + 1}`),
        relation: formDetails.get(`bandAdditionalICERelation${index + 1}`),
        sms: formDetails.get(`bandAdditionalICESMS${index + 1}Value`),
      };
    });    

    additionalICEContacts.forEach((contact, index) => {
      try {
        const validateAdditionalPhoneNumber = phoneUtil.parseAndKeepRawInput(contact.phoneNumber);
        const validAdditionalPhoneNumber = phoneUtil.isValidNumber(validateAdditionalPhoneNumber);
  
        if (!validAdditionalPhoneNumber) {
          phoneErrors.push({ name: `bandAdditionalICEPhoneNumber${index + 1}`, error: 'This is not a valid phone number.' });
        }
      } catch {
        phoneErrors.push({ name: `bandAdditionalICEPhoneNumber${index + 1}`, error: 'This is not a valid phone number.' });
      }
    });

    // If there are any errors, set them in the state
    if (phoneErrors.length > 0) {
      setPhoneNumberErrors(phoneErrors);
      return false;  // Indicating that validation failed
    }

    structureFormDetails.additionalICEContacts = additionalICEContacts;
    localStorage.setItem("activeBandStep2", JSON.stringify(structureFormDetails));
    localStorage.setItem("activeBandStep2Delete", JSON.stringify(deletedEmergencyContacts));

    navigate("/add-band/3", {state: {
      band: band, 
      profile: profile,
      medicalHistory: medicalHistory, 
      allergies: allergies, 
      medications: medications,
    }});
  };

  const addEmergencyContact = () => {
    setUpdatedEmergencyContacts([...updatedEmergencyContacts, {
      additionalICEContactId: "",
      name: "",
      phoneNumber: "",
      relation: "",
      sms: "",
    }]);
  };

  const removeEmergencyContact = (e, index) => {
    e.preventDefault(); // Prevent form submission
  
    setUpdatedEmergencyContacts((prevContacts) => {
      const contact = prevContacts[index];
  
      // Check if the contact has AdditionalICEContactId
      if (contact.AdditionalICEContactId) {
        setDeletedEmergencyContacts((prevDeleted) => [...prevDeleted, contact]);
      }
  
      // Remove the contact from updatedEmergencyContacts
      return prevContacts.filter((_, i) => i !== index);
    });
  };

  const fetchBand = async () => {
    await Sentry.startSpan(
      {
        name: "Fetch Band Data",
        op: "http.request",
      },
      async (span) => {
        try {
          const response = await fetchWithRefresh(
            `${import.meta.env.VITE_API_URL}Bands/FetchBand?bandid=${band?.BandID}`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${user?.token}`,
              },
            },
            logout,
            updateTokens,
            (message) => { toast.info(message); }
          );
  
          // Set HTTP response status in the span
          span.setAttribute("http.status_code", response.status);
  
          if (response.ok) {
            const data = await response.json();
            setBandData(data);
          } else {
            span.setStatus("error"); // Mark as error
          }
        } catch (error) {
          span.setStatus("error");
          Sentry.captureException(error);
          LogErrors(user, logout, updateTokens, error);
        } finally {
          span.end();
        }
      }
    );
  };

  const editingCoreDetails = (event) => {
    let fieldName = "";
    if(event.target){
      fieldName = event.target.name;
    } else {
      fieldName = event;
    }
    
    setOpen(true);
    setDrawerContent(
      <div className="w-full">
        <div className="flex items-center justify-between w-full">
          <br />
          <h2 className="leading-5 tracking-widest uppercase text-[12px]">
          UPDATE DETAILS
          </h2>
          <CloseIcon className='cursor-pointer' onClick={() => {
              setOpen(false);
              if(fieldName === "participantName"){
                setParticipantNameWarning(true);
              } else if (fieldName === "bandICEPhoneNumber") {
                setPrimaryICEWarning(true);
              } else if (fieldName === "bandPrimaryCondition") {
                setParticipantPrimaryWarning(true);
              }
            }} />
        </div>
        <div className="mt-6 flex flex-col gap-2 justify-stretch">
          <h3 className="text-[24px]">Update your core details?</h3>
          <p className="text-sportsDarkGrey mb-[26px]">
            If you update any of your core details, these will no longer match the details engraved on your band. We recommend that you order a new band with your updated core details.
          </p>
          <Button className="w-full" href={`/shop/my-participants`} onClick={() => setOpen(false)}>Order New Band</Button>
        </div>
      </div>
    );
  }
  const dobNotSet = () => {
    setOpen(true);
    setDrawerContent(
      <div className="w-full">
        <div className="flex items-center justify-between w-full">
          <br />
          <h2 className="leading-5 tracking-widest uppercase text-[12px]">DATE OF BIRTH</h2>
          <CloseIcon className='cursor-pointer' onClick={() => {setOpen(false);}} />
        </div>
        <div className="mt-6 flex flex-col gap-2 justify-stretch">
          <h3 className="text-[24px]">No date of birth</h3>
          <p className="text-sportsDarkGrey mb-[26px]">A date of birth is required for all participants to ensure detailed and accurate information can be provided in the case of an emergency.</p>
          <Button variant='primary' onClick={() => setOpen(false)}>Close</Button>
        </div>
      </div>
    );
  }

  const profilePictureNotSet = () => {
    setOpen(true);
    setDrawerContent(
      <div className="w-full">
        <div className="flex items-center justify-between w-full">
          <br />
          <h2 className="leading-5 tracking-widest uppercase text-[12px]">PROFILE PICTURE</h2>
          <CloseIcon className='cursor-pointer' onClick={() => {setOpen(false);}} />
        </div>
        <div className="mt-6 flex flex-col gap-2 justify-stretch">
          <h3 className="text-[24px]">No profile picture?</h3>
          <p className="text-sportsDarkGrey mb-[26px]">A profile picture is compulsory as it will help to confirm your identity in the event of an emergency.</p>
          <Button variant='primary' onClick={() => setOpen(false)}>Close</Button>
        </div>
      </div>
    );
  }

  const bronzeSubcriptionDrawer = () => {
    setOpen(true);
    setDrawerContent(
        <div className="w-full">
          <div className="flex items-center justify-between w-full">
            <br />
            <h2 className="leading-5 tracking-widest uppercase text-[12px]">
              UPGRADE
            </h2>
            <CloseIcon className='cursor-pointer' onClick={() => setOpen(false)} />
          </div>
          <div className="mt-6 flex flex-col gap-4 justify-stretch">
            <h3 className="text-[24px]">Upgrade subscription</h3>
            <p className=" text-sportsDarkGrey"> Automated emergency incident SMS to ICE contacts is only available on our silver and gold subscription levels.﻿</p>
              <Button variant="primary" onClick={() => { setOpen(false);navigate('/upgrade')}}>Upgrade Subscription</Button>
              <Button variant="quaternary" onClick={() => { setOpen(false);}}>Close</Button>
          </div>
        </div>
    )
  }

  const emergencyContactLimit = () => {
    setOpen(true);
    setDrawerContent(
        <div className="w-full">
          <div className="flex items-center justify-between w-full">
            <br />
            <h2 className="leading-5 tracking-widest uppercase text-[12px]">
              UPGRADE
            </h2>
            <CloseIcon className='cursor-pointer' onClick={() => setOpen(false)} />
          </div>
          <div className="mt-6 flex flex-col gap-4 justify-stretch">
            <h3 className="text-[24px]">Upgrade subscription</h3>
            <p className=" text-sportsDarkGrey">Your {user?.subscription?.Name} subscription level only allows you to add up to {user?.subscription?.AdditionalIceContacts} additional ICE contact{user?.subscription?.Name !== 'Bronze' ? 's' : ''} per participant. {user?.subscription?.Name !== 'Gold' ? ('To add more ICE contacts, please upgrade your subscription.') : ''}</p>
            {user?.subscription?.Name !== "Gold" && (
              <Button variant="primary" onClick={() => { setOpen(false);navigate('/upgrade')}}>Upgrade Subscription</Button>
            )}
            
            <Button variant="quaternary" onClick={() => { setOpen(false);}}>Close</Button>
          </div>
        </div>
    )
  }

  return (
    <div className="w-full min-h-svh flex flex-col justify-stretch gap-6 p-[24px] mb-[40px]">
      <ProgressBar step={2} />
      <div>
        <span className="tracking-[0.25em] text-[12px] uppercase font-semibold">
          Step 2
        </span>
        <h2>Great, let&apos;s get your details setup</h2>
      </div>
      <div className="border-dashed border-t border-[#E3E3E3]" />
      <form onSubmit={validateForm} className="space-y-4 flex flex-col justify-stretch mb-[40px]">
        <div className="flex flex-col gap-4">
          <AzureProfilePictureUpload activeAccount={profile} participant={true} temporaryImage={localStepAnswers?.profilePicture} uploadExternal={true}/>
          <p className="text-[#727272]">Your profile picture will help confirm your identity in the event of an emergency</p>
        </div>

        <div className="border-dashed border-t border-[#E3E3E3]" />

        <div>
          <TextInput
            label="Participant Name"
            name="participantName"
            required={true}
            defaultValue={localStepAnswers?.participantName || band?.ParticipantName}
            onClick={(e) => !participantNameWarning && editingCoreDetails(e)}
            readOnly={!participantNameWarning ? true : false}
          />
        </div>

        <div>
          <span className="text-sportsBlack font-medium text-base leading-[26px] mb-3 block">Participant Date of Birth*</span>
          <div className="grid grid-cols-3 gap-[6px]">
            <SelectInput 
              label="Day"
              placeholder="Select"
              name="participantDOBDay"
              defaultValue={selectedDay}
              options={days?.map((day, index) => (<option key={day + '_' + index} value={day.value}>{day.label}</option>))}
              onChange={(e) => setSelectedDay(e.target.value)}
              required={true}
            />
            <SelectInput 
              label="Month"
              placeholder="Select"
              name="participantDOBMonth"
              defaultValue={selectedMonth}
              options={months?.map((month, index) => (<option key={month + '_' + index} value={month.value}>{month.label}</option>))}
              onChange={(e) => setSelectedMonth(e.target.value)}
              required={true}
            />
            <SelectInput 
              label="Year"
              placeholder="Select"
              name="participantDOBYear"
              defaultValue={selectedYear}
              options={years?.map((year, index) => (<option key={year + '_' + index} value={year.value}>{year.label}</option>))}
              onChange={(e) => setSelectedYear(e.target.value)}
              required={true}
            />
          </div>
          <input type="hidden" name="participantDOB" value={participantDOB} />
        </div>
        
        <div>
          <TextInput
            label="Band Nickname"
            name="bandNickname"
            defaultValue={localStepAnswers?.bandNickname || band?.FriendlyName}
            required={true}
          />
        </div>

        <div className="border-dashed border-t border-[#E3E3E3]" />

        <div>
          <TextInput
            label="Primary Medical Condition"
            name="bandPrimaryCondition"
            required={true}
            defaultValue={localStepAnswers?.bandPrimaryCondition || profile?.PrimaryMedicalCondition}
            onClick={(e) => !participantPrimaryWarning && editingCoreDetails(e)}
            readOnly={!participantPrimaryWarning ? true : false}
          />
        </div>

        <div className="border-dashed border-t border-[#E3E3E3]" />

        <div>
          <TextInput
            label="Primary Emergency Contact Name (ICE)"
            name="bandICEName"
            defaultValue={localStepAnswers?.bandICEName || profile?.PrimaryIceName}
            required={true}
          />
        </div>

        <p className="text-sportsGrey">
          We recommend letting your ICE contacts know that you have listed them
          in the Sports Guardian app.
        </p>

        <AdvancedPhoneInput 
          label="Primary Emergency Contact Number"
          inputName="bandICEPhoneNumber"
          onClick={!primaryICEWarning && ( () => { editingCoreDetails("bandICEPhoneNumber")} )}
          readOnly={!primaryICEWarning ? true : false}
          defaultValue={localStepAnswers?.bandICEPhoneNumber || profile?.PrimaryIceNumber || "+44"}
          required={true}
          error={phoneNumberErrors}
        />

        <Toggle 
          name={`EmergencySMSPrimaryContact`}
          label="Automatic SMS"
          defaultChecked={localStepAnswers?.EmergencySMSPrimaryContact ? localStepAnswers?.EmergencySMSPrimaryContact : profile?.EmergencySMSPrimaryContact ? 1 : 0}
          readOnly={user?.subscription?.Type === 1 ? true : false}
          onClick={(e) => {
            if (user?.subscription?.Type === 1) {
              e.preventDefault();
              bronzeSubcriptionDrawer();
            }
          }}
        />

        <div>
          <TextInput
            label="Primary Emergency Contact Relationship"
            name="bandICERelation"
            required={true}
            defaultValue={localStepAnswers?.bandICERelation || profile?.PrimaryIceRelationship}
          />
        </div>

        {updatedEmergencyContacts.map((contact, index) => (
          <div className="flex flex-col gap-2" key={index}>
            <TextInput
              label={`Emergency Contact Name (${index + 1})`}
              name={`bandAdditionalICEName${index + 1}`}
              required={true}
              defaultValue={contact?.name || contact?.AdditionalICEName}
            />

            <AdvancedPhoneInput 
              label={`Emergency Contact Number (${index + 1})`}
              inputName={`bandAdditionalICEPhoneNumber${index + 1}`}
              defaultValue={contact?.phoneNumber || contact?.AdditionalICENumber || "+44"}
              required={true}
              error={phoneNumberErrors}
            />
            <Toggle 
              name={`bandAdditionalICESMS${index + 1}`}
              label="Automatic SMS"
              defaultChecked={contact?.sms ? contact?.sms : contact?.EmergencySMS ? 1 : 0}
              readOnly={user?.subscription?.Type === 1 ? true : false}
              onClick={(e) => {
                if (user?.subscription?.Type === 1) {
                  e.preventDefault();
                  bronzeSubcriptionDrawer();
                }
              }}
            />

            <TextInput
              label={`Emergency Contact Relationship (${index + 1})`}
              name={`bandAdditionalICERelation${index + 1}`}
              required={true}
              defaultValue={contact?.relation || contact?.AdditionalICERelationship}
            />

            <TextInput
              type={"hidden"}
              name={`bandAdditionalICEID${index + 1}`}
              defaultValue={contact?.AdditionalICEContactId || contact?.additionalICEContactId}
            />

            <Button
              variant="tertiary"
              status="nobg"
              onClick={(e) => { removeEmergencyContact(e, index); return false; }}
              className="mt-2 mb-2 text-sportsRed"
            >
              <BinIcon className="w-[24px] [&>path]:stroke-sportsRed" />
              Remove
            </Button>
          </div>
        ))}

        <Button 
          className="font-semibold" 
          variant="tertiary" 
          onClick={() => {
            updatedEmergencyContacts.length >= user?.subscription?.AdditionalIceContacts 
              ? emergencyContactLimit()
              : addEmergencyContact()
          }}
        >
          <AddIcon className="w-[15px]" />
          Add Additional ICE
        </Button>

        <div className="border-dashed border-t border-[#E3E3E3]" />
        
        <div>
          <TextArea 
            label="Notes"
            name="participantNotes"
            defaultValue={localStepAnswers?.participantNotes || profile?.Notes}
          />
        </div>

        <div className="flex flex-col border-t border-dashed border-[#E3E3E3] pt-2 !mt-6" />

        {localStepAnswers?.uploadedVoiceNote || profile?.AudioUrl ? (
          <VoiceNote existingAudio={localStepAnswers?.uploadedVoiceNote || profile?.AudioUrl} subscription={user?.subscription?.Type} editable={true} />
        ) : (
          <VoiceNote subscription={user?.subscription?.Type} editable={true} />
        )}

        <div className="flex flex-col border-t border-dashed border-[#E3E3E3] pt-6 !mt-6" />

        <Button submit={true} disabled={uploadingCDN ? true : false } status={uploadingCDN ? "disabled" : "null"}>{uploadingCDN ? "Saving..." : "Save & Continue"}</Button>
      </form>
    </div>
  );
}

Step2.propTypes = {
  band: PropTypes.shape({
    ParticipantName: PropTypes.string,
    BandColour: PropTypes.string,
    Nickname: PropTypes.string,
  }).isRequired,
};
